import { listTags } from "@/api/tag-api";
import { Button } from "@/components/ui/button";
import { CheckboxTriState, type TriState } from "@/components/ui/checkbox-tri-state";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { tagKeys, useAssignTagToContents, useTagContentsCount, useTagContentsSelectedCount, useUnassignTagFromContents } from "@/hooks/useTagQueries";
import { cn } from "@/lib/utils";
import { useSelectedItemStore } from "@/store/selectedItemStore";
import { Tag } from "@vivport/backend/types";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Search, Tag as TagIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

/** --- Types --------------------------------------------------------------- */

interface TagSelectProps {
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  contentId?: number; // manage tags for a single content item (optional)
}

interface TagSelectItemProps {
  tag: Tag;
  contentIds: number[];
  pending?: boolean; // undefined → no change; true → will assign; false → will unassign
  onToggle: (tagId: number, newValue: boolean) => void;
}

type ApiErrorPayload = {
  message?: string;
  details?: {
    fieldErrors?: Record<string, string[]>;
  };
};

function formatMutationError(error: unknown): string {
  if (error instanceof AxiosError) {
    const payload = error.response?.data as ApiErrorPayload | undefined;
    const fieldErrors = payload?.details?.fieldErrors;
    if (fieldErrors) {
      const firstField = Object.values(fieldErrors)[0];
      if (Array.isArray(firstField) && firstField.length > 0) {
        return firstField[0];
      }
    }
    if (payload?.message) {
      return payload.message;
    }
    if (error.message) {
      return error.message;
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "Unable to apply tag changes";
}

/** --- Small helper hook: child fetches its own state ---------------------- */
function useTagState(tagId: number, contentIds: number[]) {
  const { data: totalCountData } = useTagContentsCount(tagId);
  const { data: selectedCountData, isFetching } = useTagContentsSelectedCount(tagId, contentIds);

  const totalCount = totalCountData?.count ?? 0;
  const selectedCount = selectedCountData?.count ?? 0;

  const state: TriState =
    contentIds.length === 0 || selectedCount === 0
      ? "none"
      : selectedCount === contentIds.length
      ? "all"
      : "some";

  return {
    state,
    assignmentCount: state === "all" ? contentIds.length : selectedCount,
    totalCount,
    loading: isFetching,
  };
}

/** --- Child: owns its data + renders one row ------------------------------ */
function TagSelectItem({ tag, contentIds, pending, onToggle }: TagSelectItemProps) {
  const { state, assignmentCount, totalCount, loading } = useTagState(tag.id, contentIds);
  const disabled = contentIds.length === 0;

  const effectiveState: TriState = pending === undefined ? state : pending ? "all" : "none";

  const toggle = () => !disabled && onToggle(tag.id, effectiveState !== "all");

  return (
    <div
      className={cn(
        "flex items-center justify-between p-2 rounded-md transition-colors",
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-accent"
      )}
      onClick={toggle}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          toggle();
        }
      }}
    >
      <div className="flex items-center gap-2 flex-1">
        <CheckboxTriState state={effectiveState} disabled={disabled} label={`Tag ${tag.name}`} onChange={toggle} />

        <div className="flex-1">
          <div className="font-medium text-sm">
            {tag.name}
            {contentIds.length > 0 && !loading && (
              <span className="ml-1 text-xs text-muted-foreground">
                {assignmentCount} of {contentIds.length}
              </span>
            )}
            {totalCount > 0 && <span className="ml-1 text-xs text-muted-foreground">({totalCount})</span>}
          </div>
          {tag.description && <div className="text-xs text-muted-foreground truncate">{tag.description}</div>}
        </div>
      </div>
    </div>
  );
}

/** --- Parent: small, stateless wrt counts, just orchestration ------------- */
export function TagSelect({
  placeholder = "Search tags...",
  disabled,
  className,
  contentId,
}: TagSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [hasInteracted, setHasInteracted] = useState(false);
  const [pending, setPending] = useState<Map<number, boolean>>(new Map());

  const { data: tags = [], isLoading: tagsLoading } = useQuery({
    queryKey: tagKeys.list("custom"),
    queryFn: listTags,
    staleTime: 5 * 60 * 1000,
  });

  const { selectedItems } = useSelectedItemStore();
  const contentIds = useMemo<number[]>(
    () => (contentId ? [contentId] : selectedItems.map(Number).filter(Number.isFinite)),
    [contentId, selectedItems]
  );

  const assignMany = useAssignTagToContents();
  const unassignMany = useUnassignTagFromContents();

  useEffect(() => {
    if (open) {
      setHasInteracted(false);
      setPending(new Map());
    }
  }, [open]);

  // Tags flagged memberAssignable false are platform-managed: the logic layer assigns them itself,
  // so they are never offered for hand assignment.
  const assignableTags = useMemo(() => tags.filter((tag) => tag.memberAssignable !== false), [tags]);
  const filtered = useMemo(
    () =>
      !search
        ? assignableTags
        : assignableTags.filter(
            (t) =>
              t.name.toLowerCase().includes(search.toLowerCase()) ||
              t.description?.toLowerCase().includes(search.toLowerCase())
          ),
    [assignableTags, search]
  );

  const toggleTag = (tagId: number, newValue: boolean) => {
    setHasInteracted(true);
    setPending((prev) => new Map(prev).set(tagId, newValue));
  };

  const applyChanges = async () => {
    if (pending.size === 0) return;
    try {
      const entries = [...pending.entries()];
      const assignEntries = entries.filter(([, shouldAssign]) => shouldAssign);
      const unassignEntries = entries.filter(([, shouldAssign]) => !shouldAssign);

      const assignResults = await Promise.all(
        assignEntries.map(([tagId]) => assignMany.mutateAsync({ tagId, contentIds }))
      );
      await Promise.all(
        unassignEntries.map(([tagId]) => unassignMany.mutateAsync({ tagId, contentIds }))
      );

      const automationWarnings: string[] = [];
      assignResults.forEach((result) => {
        if (result.automation.matchedProcessCount > 0 && result.automation.createdProcessRunCount === 0) {
          automationWarnings.push(
            `${result.tag.name}: matched ${result.automation.matchedProcessCount} process(es) but created 0 runs`
          );
        }
        if (result.automation.enqueueFailedCount > 0) {
          const failureDetails = result.automation.enqueueFailures
            .map((failure) => `run #${failure.processRunId} (${failure.error})`)
            .join(', ');
          automationWarnings.push(
            `${result.tag.name}: ${result.automation.enqueueFailedCount}/${result.automation.enqueueAttemptedCount} run(s) failed to enqueue${failureDetails ? ` [${failureDetails}]` : ''}`
          );
        }
      });
      if (automationWarnings.length > 0) {
        toast.warning(`Automation warning: ${automationWarnings.join(' | ')}`);
      }

      setPending(new Map());
      setHasInteracted(false);
      setOpen(false);
      // Mutations handle query invalidation automatically
    } catch (error) {
      toast.error(formatMutationError(error));
    }
  };

  const header = `${contentIds.length} content${contentIds.length === 1 ? "" : "s"} selected to modify tags`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn("h-8 w-8", className)}
          disabled={disabled}
          aria-label="Manage tags"
        >
          <TagIcon className="h-4 w-4" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-80 p-0" side="bottom" sideOffset={8} align="start" avoidCollisions>
        <div className="p-3 space-y-3">
          <div>
            <div className="text-sm font-medium">Manage Tags</div>
            <div className="text-xs text-muted-foreground">{header}</div>
          </div>

          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={placeholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>

          <ScrollArea className="h-[300px]">
            {tagsLoading ? (
              <div className="p-4 text-center text-sm text-muted-foreground">Loading tags...</div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                {search ? "No tags found" : "No tags available"}
              </div>
            ) : (
              <div className="space-y-1">
                {filtered.map((tag) => (
                  <TagSelectItem
                    key={tag.id}
                    tag={tag}
                    contentIds={contentIds}
                    pending={pending.get(tag.id)}
                    onToggle={toggleTag}
                  />
                ))}
              </div>
            )}
          </ScrollArea>

          {contentIds.length > 0 && (
            <div className="text-center">
              {!hasInteracted ? (
                <div className="flex justify-center gap-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded border bg-primary border-primary" />
                    <span>All have</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 border-2 border-muted-foreground rounded bg-background flex items-center justify-center px-0.5">
                      <div className="w-1.5 h-0.5 bg-muted-foreground" />
                    </div>
                    <span>Some have</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 border-2 border-muted-foreground rounded bg-background" />
                    <span>None have</span>
                  </div>
                </div>
              ) : (
                <button className="text-sm hover:text-foreground transition-colors" onClick={applyChanges}>
                  Apply Changes
                </button>
              )}
            </div>
          )}

          <div className="pt-2 border-t">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{filtered.length} tags available</span>
              <span className="flex items-center gap-1">
                <TagIcon className="h-3 w-3" />
                Manage Tags
              </span>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
